/**
 * PropShare AI Similarity Engine
 * Analyzes prop relationships and generates intelligent recommendations
 */

class PropSimilarityService {
  constructor() {
    this.categoryWeights = {
      'Furniture': { 'Furniture': 1.0, 'Decor': 0.6, 'Lighting': 0.4 },
      'Decor': { 'Decor': 1.0, 'Furniture': 0.6, 'Textiles': 0.5 },
      'Lighting': { 'Lighting': 1.0, 'Electronics': 0.7, 'Furniture': 0.4 },
      'Textiles': { 'Textiles': 1.0, 'Costumes': 0.8, 'Decor': 0.5 },
      'Costumes': { 'Costumes': 1.0, 'Textiles': 0.8, 'Other': 0.3 },
      'Electronics': { 'Electronics': 1.0, 'Lighting': 0.7, 'Other': 0.4 },
      'Musical Instruments': { 'Musical Instruments': 1.0, 'Electronics': 0.3 },
      'Other': { 'Other': 1.0 }
    };

    this.materialGroups = {
      'organic': ['wood', 'fabric', 'leather', 'paper', 'bamboo', 'cork'],
      'metal': ['metal', 'steel', 'aluminum', 'brass', 'copper', 'iron'],
      'synthetic': ['plastic', 'rubber', 'vinyl', 'acrylic', 'polyester'],
      'mineral': ['glass', 'ceramic', 'stone', 'marble', 'concrete'],
      'composite': ['composite', 'laminate', 'plywood', 'fiberglass']
    };

    this.historicalEras = {
      'Ancient': ['Ancient', 'Classical', 'Roman', 'Greek'],
      'Medieval': ['Medieval', 'Dark Ages', 'Gothic', 'Byzantine'],
      'Renaissance': ['Renaissance', 'Baroque', 'Elizabethan'],
      'Colonial': ['Colonial', 'Georgian', 'Federal'],
      'Victorian': ['Victorian', 'Industrial', 'Steam Age'],
      'Modern': ['Modern Era', 'Art Deco', 'Mid-Century', 'Contemporary'],
      'Contemporary': ['Contemporary', 'Modern', 'Current']
    };
  }

  /**
   * Find similar props based on multiple similarity factors
   * @param {Object} targetProp - The prop to find similarities for
   * @param {Array} allProps - All available props to search through
   * @param {Object} options - Configuration options
   * @returns {Array} Sorted array of similar props with similarity scores
   */
  findSimilarProps(targetProp, allProps, options = {}) {
    const {
      maxResults = 8,
      minSimilarity = 0.15,
      excludeOwn = true,
      categoryWeight = 0.3,
      materialWeight = 0.25,
      tagsWeight = 0.25,
      historicalWeight = 0.2
    } = options;

    const similarities = [];

    for (const prop of allProps) {
      // Skip the target prop itself
      if (excludeOwn && prop.id === targetProp.id) continue;
      
      // Skip unavailable props  
      if (prop.status !== 'available') continue;

      const similarity = this.calculateSimilarity(targetProp, prop, {
        categoryWeight,
        materialWeight, 
        tagsWeight,
        historicalWeight
      });

      if (similarity >= minSimilarity) {
        similarities.push({
          prop,
          similarity,
          reasons: this.generateReasons(targetProp, prop, similarity)
        });
      }
    }

    // Sort by similarity score (highest first)
    similarities.sort((a, b) => b.similarity - a.similarity);

    return similarities.slice(0, maxResults);
  }

  /**
   * Calculate overall similarity between two props
   */
  calculateSimilarity(prop1, prop2, weights) {
    let totalScore = 0;
    let totalWeight = 0;

    // Category similarity
    const categoryScore = this.calculateCategorySimilarity(prop1, prop2);
    totalScore += categoryScore * weights.categoryWeight;
    totalWeight += weights.categoryWeight;

    // Material similarity
    const materialScore = this.calculateMaterialSimilarity(prop1, prop2);
    totalScore += materialScore * weights.materialWeight;
    totalWeight += weights.materialWeight;

    // Tags similarity
    const tagsScore = this.calculateTagsSimilarity(prop1, prop2);
    totalScore += tagsScore * weights.tagsWeight;
    totalWeight += weights.tagsWeight;

    // Historical period similarity
    const historicalScore = this.calculateHistoricalSimilarity(prop1, prop2);
    totalScore += historicalScore * weights.historicalWeight;
    totalWeight += weights.historicalWeight;

    return totalWeight > 0 ? totalScore / totalWeight : 0;
  }

  /**
   * Calculate similarity based on categories
   */
  calculateCategorySimilarity(prop1, prop2) {
    if (!prop1.category || !prop2.category) return 0;
    
    const cat1 = prop1.category;
    const cat2 = prop2.category;
    
    if (cat1 === cat2) return 1.0;
    
    // Check category relationships
    const weights = this.categoryWeights[cat1];
    return weights && weights[cat2] ? weights[cat2] : 0;
  }

  /**
   * Calculate similarity based on materials
   */
  calculateMaterialSimilarity(prop1, prop2) {
    const materials1 = this.normalizeMaterials(prop1.material);
    const materials2 = this.normalizeMaterials(prop2.material);
    
    if (materials1.length === 0 || materials2.length === 0) return 0;

    let matches = 0;
    let groupMatches = 0;
    
    for (const mat1 of materials1) {
      if (materials2.includes(mat1)) {
        matches++;
      } else {
        // Check for material group matches
        const group1 = this.getMaterialGroup(mat1);
        for (const mat2 of materials2) {
          const group2 = this.getMaterialGroup(mat2);
          if (group1 && group1 === group2) {
            groupMatches++;
            break;
          }
        }
      }
    }

    const exactScore = matches / Math.max(materials1.length, materials2.length);
    const groupScore = groupMatches / Math.max(materials1.length, materials2.length) * 0.5;
    
    return Math.min(1.0, exactScore + groupScore);
  }

  /**
   * Calculate similarity based on tags
   */
  calculateTagsSimilarity(prop1, prop2) {
    const tags1 = this.normalizeTags(prop1.tags);
    const tags2 = this.normalizeTags(prop2.tags);
    
    if (tags1.length === 0 || tags2.length === 0) return 0;

    let exactMatches = 0;
    let partialMatches = 0;
    
    for (const tag1 of tags1) {
      if (tags2.includes(tag1)) {
        exactMatches++;
      } else {
        // Check for partial/related matches
        for (const tag2 of tags2) {
          if (this.areTagsRelated(tag1, tag2)) {
            partialMatches++;
            break;
          }
        }
      }
    }

    const exactScore = exactMatches / Math.max(tags1.length, tags2.length);
    const partialScore = partialMatches / Math.max(tags1.length, tags2.length) * 0.3;
    
    return Math.min(1.0, exactScore + partialScore);
  }

  /**
   * Calculate similarity based on historical periods
   */
  calculateHistoricalSimilarity(prop1, prop2) {
    const period1 = prop1.historical_period;
    const period2 = prop2.historical_period;
    
    if (!period1 || !period2) return 0;
    if (period1 === period2) return 1.0;
    
    // Check if periods are in the same era group
    for (const periods of Object.values(this.historicalEras)) {
      const includes1 = periods.some(p => period1.includes(p));
      const includes2 = periods.some(p => period2.includes(p));
      if (includes1 && includes2) return 0.7;
    }
    
    return 0;
  }

  /**
   * Helper methods
   */
  normalizeMaterials(materials) {
    if (!materials) return [];
    if (typeof materials === 'string') {
      try {
        materials = JSON.parse(materials);
      } catch {
        return [];
      }
    }
    return Array.isArray(materials) ? materials.map(m => m.toLowerCase().trim()) : [];
  }

  normalizeTags(tags) {
    if (!tags) return [];
    if (typeof tags === 'string') {
      try {
        tags = JSON.parse(tags);
      } catch {
        return [];
      }
    }
    return Array.isArray(tags) ? tags.map(t => t.toLowerCase().trim()) : [];
  }

  getMaterialGroup(material) {
    const normalizedMaterial = material.toLowerCase();
    for (const [group, materials] of Object.entries(this.materialGroups)) {
      if (materials.some(mat => normalizedMaterial.includes(mat))) {
        return group;
      }
    }
    return null;
  }

  areTagsRelated(tag1, tag2) {
    const relatedSets = [
      ['seating', 'chair', 'bench', 'stool'],
      ['dining', 'kitchen', 'eating', 'food'],
      ['lighting', 'lamp', 'illumination', 'bright'],
      ['vintage', 'antique', 'old', 'historical'],
      ['decorative', 'ornamental', 'aesthetic', 'beautiful'],
      ['storage', 'container', 'box', 'chest'],
      ['textile', 'fabric', 'cloth', 'material']
    ];
    
    for (const set of relatedSets) {
      if (set.includes(tag1) && set.includes(tag2)) {
        return true;
      }
    }
    
    // Check for partial string matches
    if (tag1.length > 3 && tag2.length > 3) {
      return tag1.includes(tag2) || tag2.includes(tag1);
    }
    
    return false;
  }

  /**
   * Generate human-readable reasons for similarity
   */
  generateReasons(targetProp, similarProp, score) {
    const reasons = [];
    
    // Category reasons
    if (targetProp.category === similarProp.category) {
      reasons.push(`Same category (${targetProp.category})`);
    } else if (this.categoryWeights[targetProp.category]?.[similarProp.category]) {
      reasons.push(`Related category (${similarProp.category})`);
    }
    
    // Material reasons
    const materials1 = this.normalizeMaterials(targetProp.material);
    const materials2 = this.normalizeMaterials(similarProp.material);
    const sharedMaterials = materials1.filter(m => materials2.includes(m));
    if (sharedMaterials.length > 0) {
      reasons.push(`Shared materials (${sharedMaterials.join(', ')})`);
    }
    
    // Tag reasons
    const tags1 = this.normalizeTags(targetProp.tags);
    const tags2 = this.normalizeTags(similarProp.tags);
    const sharedTags = tags1.filter(t => tags2.includes(t));
    if (sharedTags.length > 0) {
      reasons.push(`Similar characteristics (${sharedTags.slice(0, 2).join(', ')})`);
    }
    
    // Historical reasons
    if (targetProp.historical_period === similarProp.historical_period) {
      reasons.push(`Same historical period`);
    }
    
    // Fallback reason based on score
    if (reasons.length === 0) {
      if (score > 0.7) reasons.push('Highly similar characteristics');
      else if (score > 0.4) reasons.push('Similar in style and use');
      else reasons.push('Complementary item');
    }
    
    return reasons.slice(0, 3); // Limit to top 3 reasons
  }

  /**
   * Get recommendations by different strategies
   */
  getRecommendationStrategies(targetProp, allProps) {
    const strategies = {};
    
    // Similar items (main algorithm)
    strategies.similar = this.findSimilarProps(targetProp, allProps, {
      maxResults: 4,
      categoryWeight: 0.4,
      materialWeight: 0.3,
      tagsWeight: 0.3,
      historicalWeight: 0
    });
    
    // Same era items
    if (targetProp.historical_period) {
      strategies.sameEra = this.findSimilarProps(targetProp, allProps, {
        maxResults: 4,
        categoryWeight: 0.1,
        materialWeight: 0.1,
        tagsWeight: 0.2,
        historicalWeight: 0.6,
        minSimilarity: 0.3
      });
    }
    
    // Complementary items (different category, similar tags/materials)
    strategies.complementary = this.findSimilarProps(targetProp, allProps, {
      maxResults: 4,
      categoryWeight: -0.2, // Negative weight to prefer different categories
      materialWeight: 0.4,
      tagsWeight: 0.4,
      historicalWeight: 0.4,
      minSimilarity: 0.2
    });
    
    // Popular items from same organization
    const sameOrgProps = allProps.filter(p => 
      p.owner === targetProp.owner && 
      p.id !== targetProp.id &&
      p.status === 'available'
    );
    strategies.fromSameOrg = sameOrgProps
      .sort((a, b) => (b.checkout_count || 0) - (a.checkout_count || 0))
      .slice(0, 4)
      .map(prop => ({
        prop,
        similarity: 0.5,
        reasons: [`Popular at ${prop.owner}`]
      }));
    
    return strategies;
  }
}

const propSimilarityService = new PropSimilarityService();
export default propSimilarityService;
